import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CANDY_WRAPPER_COMPONENT_ADDRESS,
  RADIX_API_GATEWAY_URL,
} from "../../constants";
import { useRadixConnect } from "../../radix_connect";
import {
  CANDY_WRAPPER_PAIRS,
  HAS_WRAPPER_COMPONENT_ADDRESS,
  getCandyWrapperPair,
} from "../data/candyWrapperConfig";

const STORAGE_KEYS = {
  SESSION_RECEIPTS: "wrapper_studio_session_receipts_v2",
};

const GAME_STATUS = {
  IDLE: "IDLE",
  CONFIG_PENDING: "CONFIG_PENDING",
  SUBMITTING: "SUBMITTING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
};

const createComponentError = (message, code) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

const buildComponentState = (overrides = {}) => ({
  loading: Boolean(CANDY_WRAPPER_COMPONENT_ADDRESS),
  componentExists: false,
  blueprintName: "",
  livePairAddresses: [],
  livePairCount: 0,
  candiesCouplesStoreAddress: "",
  ledgerStateVersion: null,
  verifiedAt: null,
  lastCheckedAt: null,
  hasVerifiedState: false,
  error: null,
  ...overrides,
});

const getReceiptStorageAccountKey = (accountAddress) =>
  accountAddress || "__disconnected__";

const loadAllSessionReceipts = () => {
  try {
    const rawValue = localStorage.getItem(STORAGE_KEYS.SESSION_RECEIPTS);
    if (!rawValue) {
      return {};
    }

    const parsed = JSON.parse(rawValue);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const loadSessionReceipts = (accountAddress) =>
  loadAllSessionReceipts()[getReceiptStorageAccountKey(accountAddress)] || [];

const saveSessionReceipts = (accountAddress, receipts) => {
  try {
    const allReceipts = loadAllSessionReceipts();
    allReceipts[getReceiptStorageAccountKey(accountAddress)] = receipts;
    localStorage.setItem(
      STORAGE_KEYS.SESSION_RECEIPTS,
      JSON.stringify(allReceipts)
    );
  } catch (error) {
    console.warn("Failed to store wrapper session receipts:", error);
  }
};

const getWalletErrorMessage = (error) => {
  if (!error) {
    return "Wrapping failed before the transaction could be submitted.";
  }

  const rawMessage =
    typeof error === "string"
      ? error
      : error.message || "Wrapping failed before the transaction could be submitted.";
  const normalized = rawMessage.toLowerCase();

  if (normalized.includes("rejected") || normalized.includes("cancel")) {
    return "Wrapping was cancelled in wallet.";
  }

  if (normalized.includes("insufficient")) {
    return "Wallet reported insufficient balance for this wrap.";
  }

  return rawMessage;
};

const parseWrapAmount = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return NaN;
  }

  if (!/^\d+$/.test(trimmed)) {
    return NaN;
  }

  return Number.parseInt(trimmed, 10);
};

const buildAvailablePairs = (livePairAddresses = []) => {
  const livePairAddressSet = new Set(livePairAddresses);
  const configuredLivePairs = CANDY_WRAPPER_PAIRS
    .filter((pair) => livePairAddressSet.has(pair.unwrappedResourceAddress))
    .map((pair) => ({
      ...pair,
      isLivePair: true,
      isConfigured: true,
    }));

  const knownAddresses = new Set(
    configuredLivePairs.map((pair) => pair.unwrappedResourceAddress)
  );

  const liveOnlyPairs = Array.from(livePairAddressSet)
    .filter((address) => !knownAddresses.has(address))
    .map((address) => ({
      ...getCandyWrapperPair(address),
      isLivePair: true,
      isConfigured: true,
    }));

  return [...configuredLivePairs, ...liveOnlyPairs];
};

const getDefaultPairAddress = (pairs = []) =>
  pairs.find((pair) => pair.isConfigured)?.unwrappedResourceAddress ||
  pairs[0]?.unwrappedResourceAddress ||
  "";

export const useCandyWrapper = () => {
  const { rdt, selectedAccount, connected } = useRadixConnect();
  const [selectedPairAddress, setSelectedPairAddress] = useState(
    ""
  );
  const [wrapAmount, setWrapAmount] = useState("1");
  const [gameStatus, setGameStatus] = useState(
    CANDY_WRAPPER_COMPONENT_ADDRESS ? GAME_STATUS.IDLE : GAME_STATUS.CONFIG_PENDING
  );
  const [error, setError] = useState(null);
  const [latestReceipt, setLatestReceipt] = useState(null);
  const [sessionReceipts, setSessionReceipts] = useState(() =>
    loadSessionReceipts(selectedAccount?.address)
  );
  const [componentState, setComponentState] = useState(() => buildComponentState());
  const [balanceState, setBalanceState] = useState({
    loading: false,
    amount: 0,
    error: null,
  });

  const availablePairs = useMemo(
    () => buildAvailablePairs(componentState.livePairAddresses),
    [componentState.livePairAddresses]
  );
  const selectedPair =
    availablePairs.find(
      (pair) => pair.unwrappedResourceAddress === selectedPairAddress
    ) || null;
  const configuredPairs = availablePairs;
  const hasLiveWrapperConfig = configuredPairs.length > 0;
  const normalizedAmount = parseWrapAmount(wrapAmount);
  const isAmountValid = Number.isInteger(normalizedAmount) && normalizedAmount > 0;
  const hasEnoughBalance =
    isAmountValid &&
    !balanceState.error &&
    Number(balanceState.amount) >= normalizedAmount;
  const isWrapping = gameStatus === GAME_STATUS.SUBMITTING;
  const isConfigReady = Boolean(
    CANDY_WRAPPER_COMPONENT_ADDRESS && selectedPair?.isConfigured
  );
  const restingStatus =
    !CANDY_WRAPPER_COMPONENT_ADDRESS
      ? GAME_STATUS.CONFIG_PENDING
      : componentState.loading
        ? GAME_STATUS.IDLE
        : componentState.componentExists
          ? GAME_STATUS.IDLE
          : GAME_STATUS.CONFIG_PENDING;
  const restingStatusRef = useRef(restingStatus);
  const componentStateRef = useRef(componentState);

  useEffect(() => {
    restingStatusRef.current = restingStatus;
  }, [restingStatus]);

  useEffect(() => {
    componentStateRef.current = componentState;
  }, [componentState]);

  const appendReceipt = useCallback((receipt) => {
    setSessionReceipts((previous) => {
      const next = [receipt, ...previous].slice(0, 12);
      saveSessionReceipts(selectedAccount?.address, next);
      return next;
    });
  }, [selectedAccount?.address]);

  const refreshComponentState = useCallback(async () => {
    if (!CANDY_WRAPPER_COMPONENT_ADDRESS) {
      const emptyState = buildComponentState({ loading: false });
      setComponentState(emptyState);
      return emptyState;
    }

    setComponentState((previous) => ({
      ...previous,
      loading: true,
      error: null,
    }));

    try {
      const detailsResponse = await fetch(
        `${RADIX_API_GATEWAY_URL}/state/entity/details`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            addresses: [CANDY_WRAPPER_COMPONENT_ADDRESS],
            aggregation_level: "Global",
          }),
        }
      );

      if (!detailsResponse.ok) {
        throw new Error("Gateway wrapper lookup failed.");
      }

      const detailsData = await detailsResponse.json();
      const componentItem = detailsData.items?.[0];

      if (!componentItem) {
        throw createComponentError(
          "The configured wrapper component was not found on this network.",
          "component-not-found"
        );
      }

      const blueprintName = componentItem.details?.blueprint_name || "";
      const candiesCouplesStoreAddress =
        componentItem.details?.state?.fields?.find(
          (field) => field.field_name === "candies_couples"
        )?.value || "";

      if (blueprintName !== "CandyWrapper" || !candiesCouplesStoreAddress) {
        throw createComponentError(
          "The configured component is not a valid CandyWrapper.",
          "invalid-wrapper-component"
        );
      }

      let nextCursor = null;
      let livePairCount = 0;
      let livePairAddresses = [];
      let ledgerStateVersion =
        detailsData.ledger_state?.state_version || null;

      do {
        const keysResponse = await fetch(
          `${RADIX_API_GATEWAY_URL}/state/key-value-store/keys`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              key_value_store_address: candiesCouplesStoreAddress,
              ...(nextCursor ? { cursor: nextCursor } : {}),
            }),
          }
        );

        if (!keysResponse.ok) {
          throw new Error("Gateway pair lookup failed.");
        }

        const keysData = await keysResponse.json();
        livePairCount = Number(keysData.total_count || 0);
        ledgerStateVersion =
          keysData.ledger_state?.state_version || ledgerStateVersion;

        const pageAddresses = (keysData.items || [])
          .map((item) => item.key?.programmatic_json?.value)
          .filter((value) => typeof value === "string");

        livePairAddresses = [...livePairAddresses, ...pageAddresses];
        nextCursor = keysData.next_cursor || null;
      } while (nextCursor);

      const nextState = {
        ...buildComponentState({
          loading: false,
          componentExists: true,
          blueprintName,
          livePairAddresses: Array.from(new Set(livePairAddresses)),
          livePairCount,
          candiesCouplesStoreAddress,
          ledgerStateVersion,
          verifiedAt: new Date().toISOString(),
          lastCheckedAt: new Date().toISOString(),
          hasVerifiedState: true,
          error: null,
        }),
      };

      setComponentState(nextState);
      return nextState;
    } catch (componentError) {
      console.error("Failed to refresh wrapper component state:", componentError);
      const errorMessage =
        componentError?.message || "Failed to refresh live wrapper state.";
      const isHardFailure =
        componentError?.code === "component-not-found" ||
        componentError?.code === "invalid-wrapper-component";

      const nextState = isHardFailure
        ? buildComponentState({
          loading: false,
          lastCheckedAt: new Date().toISOString(),
          error: errorMessage,
        })
        : null;

      setComponentState((previous) =>
        nextState || {
          ...previous,
          loading: false,
          lastCheckedAt: new Date().toISOString(),
          error: errorMessage,
        }
      );

      if (nextState) {
        return nextState;
      }

      return {
        ...componentStateRef.current,
        loading: false,
        lastCheckedAt: new Date().toISOString(),
        error: errorMessage,
      };
    }
  }, []);

  const refreshSelectedBalance = useCallback(async () => {
    if (!selectedAccount?.address || !selectedPair?.unwrappedResourceAddress) {
      setBalanceState({ loading: false, amount: 0, error: null });
      return 0;
    }

    setBalanceState((previous) => ({ ...previous, loading: true, error: null }));

    try {
      let cursor = null;
      let stateVersion = null;
      let amount = 0;
      let found = false;

      do {
        const requestBody = { address: selectedAccount.address };
        if (cursor) {
          requestBody.cursor = cursor;
          requestBody.at_ledger_state = { state_version: stateVersion };
        }

        const response = await fetch(
          `${RADIX_API_GATEWAY_URL}/state/entity/page/fungibles/`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error("Gateway balance lookup failed.");
        }

        const data = await response.json();
        const items = data.items || [];

        for (const item of items) {
          if (
            item.resource_address === selectedPair.unwrappedResourceAddress
          ) {
            amount = Number.parseFloat(item.amount) || 0;
            found = true;
            break;
          }
        }

        cursor = data.next_cursor || null;
        stateVersion = data.ledger_state?.state_version || null;
      } while (cursor && !found);

      setBalanceState({ loading: false, amount, error: null });
      return amount;
    } catch (balanceError) {
      console.error("Failed to fetch wrapper balance:", balanceError);
      setBalanceState({
        loading: false,
        amount: 0,
        error: balanceError?.message || "Balance lookup failed.",
      });
      return null;
    }
  }, [selectedAccount, selectedPair]);

  useEffect(() => {
    if (availablePairs.length === 0 && selectedPairAddress) {
      setSelectedPairAddress("");
      return;
    }

    if ((!selectedPairAddress || !selectedPair) && availablePairs.length > 0) {
      const defaultPairAddress = getDefaultPairAddress(availablePairs);

      if (defaultPairAddress) {
        setSelectedPairAddress(defaultPairAddress);
      }
    }
  }, [availablePairs, selectedPair, selectedPairAddress]);

  useEffect(() => {
    refreshComponentState();
  }, [refreshComponentState]);

  useEffect(() => {
    setLatestReceipt(null);
    setError(null);
    setSessionReceipts(loadSessionReceipts(selectedAccount?.address));
    setGameStatus(restingStatusRef.current);
  }, [selectedAccount?.address]);

  useEffect(() => {
    if (
      gameStatus !== GAME_STATUS.SUBMITTING &&
      gameStatus !== GAME_STATUS.SUCCESS &&
      gameStatus !== GAME_STATUS.ERROR
    ) {
      setGameStatus(restingStatus);
    }
  }, [gameStatus, restingStatus]);

  useEffect(() => {
    refreshSelectedBalance();
  }, [refreshSelectedBalance]);

  const selectPair = useCallback((unwrappedResourceAddress) => {
    setSelectedPairAddress(unwrappedResourceAddress);
    setLatestReceipt(null);
    setError(null);
    setGameStatus(restingStatus);
  }, [restingStatus]);

  const clearLatestReceipt = useCallback(() => {
    setLatestReceipt(null);
    setGameStatus(restingStatus);
  }, [restingStatus]);

  const clearError = useCallback(() => {
    setError(null);
    setGameStatus(restingStatus);
  }, [restingStatus]);

  const clearSessionReceipts = useCallback(() => {
    setSessionReceipts([]);
    saveSessionReceipts(selectedAccount?.address, []);
  }, [selectedAccount?.address]);

  const wrapCandy = useCallback(async () => {
    if (isWrapping) {
      return { success: false, reason: "already-submitting" };
    }

    if (!connected || !selectedAccount?.address) {
      setError("Connect a Radix wallet before using the Candy Wrapper.");
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "missing-account" };
    }

    if (!rdt?.walletApi) {
      setError("Wallet connection is still initializing. Try again in a moment.");
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "wallet-not-ready" };
    }

    if (componentState.loading) {
      setError("Checking the wrapper on-chain. Try again in a moment.");
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "component-loading" };
    }

    if (HAS_WRAPPER_COMPONENT_ADDRESS && !componentState.componentExists) {
      setError(
        componentState.error ||
        "The configured wrapper component could not be verified on this network."
      );
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "component-missing" };
    }

    if (!selectedPair) {
      setError(
        hasLiveWrapperConfig
          ? "Choose a candy pair before starting the candy wrap."
          : "No wrapped candies are loaded right now."
      );
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "missing-pair" };
    }

    if (!isConfigReady) {
      setError(
        HAS_WRAPPER_COMPONENT_ADDRESS
          ? hasLiveWrapperConfig
            ? "That candy is not currently available in the wrapper."
            : "No wrapped candies are loaded right now."
          : "Candy Wrapper is not available right now."
      );
      setGameStatus(
        hasLiveWrapperConfig ? GAME_STATUS.ERROR : GAME_STATUS.CONFIG_PENDING
      );
      return { success: false, reason: "config-pending" };
    }

    if (!isAmountValid) {
      setError("Enter a whole candy amount greater than zero.");
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "invalid-amount" };
    }

    const freshBalance = await refreshSelectedBalance();
    if (freshBalance === null) {
      setError("Could not verify your wallet balance right now. Try again in a moment.");
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "balance-unavailable" };
    }

    if (freshBalance < normalizedAmount) {
      setError(
        `You only have ${freshBalance} ${selectedPair.beforeLabel} available in this wallet.`
      );
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "insufficient-balance" };
    }

    setError(null);
    setGameStatus(GAME_STATUS.SUBMITTING);

    const manifest = `
CALL_METHOD
    Address("${selectedAccount.address}")
    "withdraw"
    Address("${selectedPair.unwrappedResourceAddress}")
    Decimal("${normalizedAmount}")
;
TAKE_ALL_FROM_WORKTOP
    Address("${selectedPair.unwrappedResourceAddress}")
    Bucket("unwrapped_candy")
;
CALL_METHOD
    Address("${CANDY_WRAPPER_COMPONENT_ADDRESS}")
    "wrap_candy"
    Bucket("unwrapped_candy")
;
CALL_METHOD
    Address("${selectedAccount.address}")
    "deposit_batch"
    Expression("ENTIRE_WORKTOP")
;
    `.trim();

    try {
      const result = await rdt.walletApi.sendTransaction({
        transactionManifest: manifest,
        version: 1,
      });

      if (result.isErr()) {
        setError(getWalletErrorMessage(result.error));
        setGameStatus(GAME_STATUS.ERROR);
        return { success: false, reason: "wallet-error" };
      }

      const transactionIntentHash = result.value?.transactionIntentHash || null;

      if (!transactionIntentHash) {
        setError("Transaction submitted, but no intent hash was returned by the wallet.");
        setGameStatus(GAME_STATUS.ERROR);
        return { success: false, reason: "missing-intent-hash" };
      }

      const receipt = {
        id: `${transactionIntentHash}-${Date.now()}`,
        transactionIntentHash,
        submittedAt: new Date().toISOString(),
        accountAddress: selectedAccount.address,
        amount: normalizedAmount,
        pairSlug: selectedPair.slug,
        pairDisplayName: selectedPair.displayName,
        family: selectedPair.family,
        version: selectedPair.version,
        beforeLabel: selectedPair.beforeLabel,
        afterLabel: selectedPair.afterLabel,
        accent: selectedPair.accent,
        glow: selectedPair.glow,
        versionGradient: selectedPair.versionGradient,
        unwrappedResourceAddress: selectedPair.unwrappedResourceAddress,
        wrappedResourceAddress: selectedPair.wrappedResourceAddress,
      };

      setLatestReceipt(receipt);
      appendReceipt(receipt);
      setGameStatus(GAME_STATUS.SUCCESS);
      await refreshSelectedBalance();

      return { success: true, receipt };
    } catch (walletError) {
      setError(getWalletErrorMessage(walletError));
      setGameStatus(GAME_STATUS.ERROR);
      return { success: false, reason: "wallet-throw" };
    }
  }, [
    appendReceipt,
    connected,
    isAmountValid,
    isConfigReady,
    isWrapping,
    normalizedAmount,
    rdt,
    componentState.componentExists,
    componentState.error,
    componentState.loading,
    hasLiveWrapperConfig,
    refreshSelectedBalance,
    selectedAccount,
    selectedPair,
  ]);

  return {
    selectedPair,
    selectedPairAddress,
    wrapAmount,
    setWrapAmount,
    configuredPairs,
    availablePairs,
    latestReceipt,
    sessionReceipts,
    balanceState,
    error,
    connected,
    gameStatus,
    isWrapping,
    isConfigReady,
    hasWrapperComponentAddress: HAS_WRAPPER_COMPONENT_ADDRESS,
    hasLiveWrapperConfig,
    componentState,
    isAmountValid,
    hasEnoughBalance,
    canSubmit:
      Boolean(selectedPair) &&
      isConfigReady &&
      connected &&
      isAmountValid &&
      hasEnoughBalance &&
      !isWrapping,
    GAME_STATUS,
    selectPair,
    wrapCandy,
    refreshComponentState,
    refreshSelectedBalance,
    clearError,
    clearLatestReceipt,
    clearSessionReceipts,
  };
};

export default useCandyWrapper;
